var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useState, useCallback } from 'react';
import { UXPContext } from '@core/context/Context';
import { filterUniqueWidgets, getComponents, hasValue } from '@utils';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
// v2: cached items must carry appRoles/userGroups — bumped so pre-permission caches are ignored
const CACHE_KEY = 'uxpcore_widgetdrawer_cache_v2';
/**
 * Load cache from localStorage
 */
function loadCacheFromStorage() {
    try {
        localStorage.removeItem('uxpcore_widgetdrawer_cache'); // clean up pre-v2 orphan
        const stored = localStorage.getItem(CACHE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Validate cache age
            if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_DURATION) {
                console.log('[useWidgetCache] Loaded valid cache from localStorage');
                return parsed;
            }
            else {
                console.log('[useWidgetCache] Cache expired, will fetch fresh data');
            }
        }
    }
    catch (e) {
        console.error('[useWidgetCache] Failed to load cache from storage:', e);
    }
    return { widgets: [], uis: [], categories: [], timestamp: null };
}
/**
 * Save cache to localStorage
 */
function saveCacheToStorage(cache) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
        console.log('[useWidgetCache] Saved cache to localStorage');
    }
    catch (e) {
        console.error('[useWidgetCache] Failed to save cache to storage:', e);
    }
}
// Initialize module-level cache from localStorage
const cache = loadCacheFromStorage();
/**
 * @export
 * Low-level hook for managing widget/UI cache with localStorage persistence
 * Single source of truth for widget data across the application
 */
export function useWidgetCache(options = {}) {
    var _a;
    const context = useContext(UXPContext);
    const loadWidgetsFromIviva = context.loadWidgetsFromIviva || false;
    const enableUIs = (_a = options.enableUIs) !== null && _a !== void 0 ? _a : true;
    // State
    const [allWidgets, setAllWidgets] = useState(cache.widgets);
    const [allUIs, setAllUIs] = useState(cache.uis);
    const [allCategories, setAllCategories] = useState(cache.categories);
    const [loading, setLoading] = useState(true);
    // Load data on mount (with cache check)
    useEffect(() => {
        // Dev mode: always load fresh from local registry (synchronous, no caching needed)
        if (!loadWidgetsFromIviva) {
            loadData();
            return;
        }
        const isValid = cache.timestamp && Date.now() - cache.timestamp < CACHE_DURATION;
        if (isValid && cache.widgets.length > 0) {
            // Use cached data
            console.log('[useWidgetCache] Using cached data');
            setAllWidgets(cache.widgets);
            setAllUIs(cache.uis);
            setAllCategories(cache.categories);
            setLoading(false);
            return;
        }
        // Cache invalid or empty - load fresh data
        console.log('[useWidgetCache] Cache invalid, loading fresh data');
        loadData();
    }, []);
    // Re-load when a new component is registered after mount (e.g. lazy-loaded app scripts)
    useEffect(() => {
        let timer;
        const handler = () => {
            clearTimeout(timer);
            timer = setTimeout(() => loadData(), 300);
        };
        window.addEventListener('uxp:component-registered', handler);
        return () => {
            window.removeEventListener('uxp:component-registered', handler);
            clearTimeout(timer);
        };
    }, []);
    /**
     * Load all data (widgets and UIs)
     */
    function loadData() {
        return __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            const [loadedWidgets, loadedUIs, loadedCategories] = yield loadComponents();
            // Update state
            setAllWidgets(loadedWidgets);
            setAllCategories(loadedCategories);
            setAllUIs(loadedUIs);
            // Only persist to cache when loading from marketplace (not needed in dev mode)
            if (loadWidgetsFromIviva) {
                cache.widgets = loadedWidgets;
                cache.uis = loadedUIs;
                cache.categories = loadedCategories;
                cache.timestamp = Date.now();
                saveCacheToStorage(cache);
                console.log('[useWidgetCache] Cache updated after fresh data load');
            }
            setLoading(false);
        });
    }
    /**
     * Load all components (widgets + UIs) from marketplace or dev environment.
     * Returns [widgets, uis, categories]
     */
    function loadComponents() {
        return __awaiter(this, void 0, void 0, function* () {
            if (loadWidgetsFromIviva) {
                const [widgets, uis, categories] = yield loadFromMarketplace();
                return [widgets, enableUIs ? uis : [], categories];
            }
            else {
                const [widgets, uis, categories] = loadFromDevEnvironment();
                return [widgets, enableUIs ? uis : [], categories];
            }
        });
    }
    /**
     * Load components from dev environment (local registry).
     * Returns [widgets, uis, categories]
     */
    function loadFromDevEnvironment() {
        const widgets = getComponents('widget').map((w) => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description) })));
        const uis = getComponents('ui')
            .filter((u) => { var _a; return !((_a = u === null || u === void 0 ? void 0 : u.id) === null || _a === void 0 ? void 0 : _a.startsWith('uxp-widget-designer-bundle')); })
            .map((u) => (Object.assign(Object.assign({}, u), { localizedName: context.$L(u.name), localizedDescription: context.$L(u.description) })));
        return [widgets, uis, []]; // No categories in dev mode
    }
    /**
     * Load all components from marketplace (search-components returns widgets + UIs + backgrounds).
     * Returns [widgets, uis, categories]
     */
    function loadFromMarketplace() {
        return __awaiter(this, void 0, void 0, function* () {
            const [categories, componentData] = yield Promise.all([
                fetchCategories(),
                fetchWidgets()
            ]);
            const allComponents = (componentData || []).map((w) => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description), category: w.category || 'user-defined' })));
            // Split by mode — search-components returns all types in one response
            const widgets = allComponents
                .filter((w) => !w.modes || w.modes.includes('widget'))
                .sort((a, b) => (a.category || '').localeCompare(b.category || ''));
            const uis = allComponents.filter((w) => { var _a; return (_a = w.modes) === null || _a === void 0 ? void 0 : _a.includes('ui'); });
            // Inject all locally registered widgets not returned by the marketplace API
            // (covers app-specific widgets, built-ins, and any widget registered after mount)
            const registeredIds = new Set(widgets.map(w => w.id));
            const builtIns = getComponents('widget')
                .filter(w => !registeredIds.has(w.id) && !registeredIds.has(w.normalizedId))
                .map(w => (Object.assign(Object.assign({}, w), { localizedName: context.$L(w.name), localizedDescription: context.$L(w.description || ''), category: w.category || 'app-widget' })));
            const enhancedCategories = enhanceCategories(categories);
            return [[...builtIns, ...widgets], uis, enhancedCategories];
        });
    }
    /**
     * Fetch widgets from API
     */
    function fetchWidgets() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = hasValue(context === null || context === void 0 ? void 0 : context.widgetSearchAPI) ? context.widgetSearchAPI : '/api/uxp/search-components';
            const { data } = yield context.executeAPISafe({
                url,
                method: 'POST',
                headers: {
                    'Authorization': context.apiKey ? `APIKEY ${context.apiKey}` : '',
                }
            });
            return data ? filterUniqueWidgets(data) : [];
        });
    }
    /**
     * Fetch categories from API
     */
    function fetchCategories() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = 'https://s3-ap-southeast-1.amazonaws.com/marketplace.iviva/widget-categories.json';
            try {
                const res = yield fetch(url);
                if (!res.ok)
                    throw new Error(`HTTP ${res.status}`);
                const data = yield res.json();
                return data.map((c) => (Object.assign(Object.assign({}, c), { code: `uxp-core.widget-category.${c.id}` })));
            }
            catch (e) {
                console.error('Unable to get categories. Error: ', e);
                return [];
            }
        });
    }
    /**
     * Enhance categories with __all__ and user-defined
     */
    function enhanceCategories(categories) {
        if (categories.length === 0)
            return [];
        const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));
        return [
            { id: '__all__', code: 'uxp-core.widget-category.__all__', name: 'All', icon: '' },
            {
                id: 'user-defined',
                code: 'uxp-core.widget-category.user-defined',
                name: 'My Widgets',
                icon: '',
                subCategories: ['user-defined']
            },
            ...sorted
        ];
    }
    /**
     * Invalidate cache and clear data
     */
    const invalidateCache = useCallback(() => {
        cache.widgets = [];
        cache.uis = [];
        cache.categories = [];
        cache.timestamp = null;
        try {
            localStorage.removeItem(CACHE_KEY);
            console.log('[useWidgetCache] Cache invalidated');
        }
        catch (e) {
            console.error('[useWidgetCache] Failed to remove cache from storage:', e);
        }
        setAllWidgets([]);
        setAllUIs([]);
        setAllCategories([]);
    }, []);
    /**
     * Reload all data
     */
    const reload = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        console.log('[useWidgetCache] Manual reload triggered');
        invalidateCache();
        yield loadData();
    }), [invalidateCache]);
    return {
        allWidgets,
        allUIs,
        allCategories,
        isLoading: loading,
        reload,
        invalidateCache
    };
}
export default useWidgetCache;
